/*
 * Copyright (c) Qalgo GmbH. All rights reserved.
 * Developers: Adrian Engelmann, Kiran Jayaramu, Nicu Bratescu
 * Name:          Guru Nudge
 * Description:   Selects optional, localized and context-aware first questions for Qeelea chats
 */

const normalize = value => String(value || '').trim();

const normalizeRoles = value => {
	const entries = Array.isArray(value) ? value : String(value || '').split(/[;,]/);
	return entries.map(role => normalize(role).replace(/^\[|\]$/g, '').toUpperCase()).filter(Boolean);
};

const matchesRoles = (entry, contextRoles) => {
	const userRoles = normalizeRoles(contextRoles);
	// ADMIN is an application-wide override, matching the established Synapcus
	// authorization convention. Tool/XR authorization still protects the data.
	if (userRoles.includes('ADMIN')) return true;
	const rolesAny = normalizeRoles(entry.rolesAny);
	const rolesAll = normalizeRoles(entry.rolesAll);
	const rolesNone = normalizeRoles(entry.rolesNone);
	if (rolesNone.some(role => userRoles.includes(role))) return false;
	if (rolesAll.length && !rolesAll.every(role => userRoles.includes(role))) return false;
	return !rolesAny.length || rolesAny.some(role => userRoles.includes(role));
};

const matchesPage = (entry, page) => {
	const include = Array.isArray(entry.pageIncludes) ? entry.pageIncludes : [];
	const exclude = Array.isArray(entry.pageExcludes) ? entry.pageExcludes : [];
	const normalizedPage = normalize(page).toLowerCase();
	if (exclude.some(value => normalizedPage.includes(normalize(value).toLowerCase()))) return false;
	return include.length === 0 || include.some(value => normalizedPage.includes(normalize(value).toLowerCase()));
};

const hasDominoDocumentContext = (page, currentObject) => {
	const object = currentObject && typeof currentObject === 'object' ? currentObject : {};
	const trustedObjectId = normalize(object.objectUnid || object.unid);
	if (/^[0-9a-f]{32}$/i.test(trustedObjectId)) return true;
	// Nudge selection is only a UI convenience. The actual object is resolved
	// and authorized again by Synforge/Domino. Requiring a Domino UNID here
	// prevents a list XPage whose name contains a form name from receiving an
	// object-specific question.
	return /(?:[?&])documentid=([0-9a-f]{32})(?=[&#\s]|$)/i.test(normalize(page));
};

const matchesContextType = (entry, page, currentObject) => {
	const configuredType = normalize(entry.contextType).toLowerCase();
	if (!configuredType || configuredType === 'any') return true;
	const hasDocument = hasDominoDocumentContext(page, currentObject);
	if (configuredType === 'detail' || configuredType === 'form') return hasDocument;
	if (configuredType === 'list' || configuredType === 'view') return !hasDocument;
	// Unknown optional configuration fails closed instead of leaking a wrong
	// question into the host page.
	return false;
};

const requiredToolsFor = (config, entry) => {
	const dashboardGuard = config?.chat_dashboard?.capabilityGuard;
	const guard = config?.capabilityGuard && typeof config.capabilityGuard === 'object'
		? config.capabilityGuard
		: (dashboardGuard && typeof dashboardGuard === 'object' ? dashboardGuard : {});
	const allowedTools = new Set(
		(Array.isArray(guard.allowedTools) ? guard.allowedTools : []).map(normalize).filter(Boolean)
	);
	const configured = guard.questionTools && typeof guard.questionTools === 'object'
		? guard.questionTools[normalize(entry?.id)] : null;
	const requiredTools = (Array.isArray(entry?.requiredTools) ? entry.requiredTools : configured)
		?.map(normalize).filter(Boolean) || [];

	// Public website assistants do not use the authenticated Synapcus tool
	// registry. Authenticated suggestions, however, fail closed: a question is
	// useful only when every tool needed for its complete answer is allow-listed.
	if (config?.chat_cfg_name !== 'synapcus-chat-guru') return requiredTools;
	if (!requiredTools.length || requiredTools.some(tool => !allowedTools.has(tool))) return null;
	return requiredTools;
};

const stableIndex = (value, length) => {
	let hash = 2166136261;
	const text = String(value || '');
	for (let index = 0; index < text.length; index += 1) {
		hash ^= text.charCodeAt(index);
		hash = Math.imul(hash, 16777619);
	}
	return length ? (hash >>> 0) % length : 0;
};

const contextIdentity = (context, page) => {
	const currentObject = context && context.currentObject && typeof context.currentObject === 'object'
		? context.currentObject : {};
	const objectType = normalize(currentObject.objectType || currentObject.type).toLowerCase();
	const objectId = normalize(currentObject.objectUnid || currentObject.unid ||
		currentObject.objectNumber || currentObject.number || currentObject.id).toLowerCase();
	return objectType && objectId ? `object:${objectType}:${objectId}` : normalize(page).toLowerCase();
};

export const mergeGuruPageQuestion = (questions, pageQuestion) => {
	const source = Array.isArray(questions) ? questions : [];
	if (!pageQuestion || !normalize(pageQuestion.question)) return source.slice();
	let replaced = false;
	const result = source.map(item => {
		if (!item || item.answer !== 'SynapcusAktuelleMaske') return item;
		replaced = true;
		return {
			...item,
			question: pageQuestion.question,
			// Initial options use `text`; follow-up questions use `question`.
			// Maintaining both keeps an already opened chat synchronized after a
			// form/list page change.
			text: pageQuestion.question,
			prompt: pageQuestion.prompt || pageQuestion.question,
			guruNudge: true
		};
	});
	return replaced ? result : [pageQuestion, ...result];
};

export const selectGuruNudges = (chatConfig, context = {}, limit = 5) => {
	try {
		const config = chatConfig && typeof chatConfig === 'object' ? chatConfig : {};
		const entries = Array.isArray(config.chat_nudge_questions) ? config.chat_nudge_questions : [];
		if (!entries.length) return [];

		const page = normalize(context.currentPage || context.page || context.referrer || '');
		const currentObject = context.currentObject && typeof context.currentObject === 'object'
			? context.currentObject : null;
		const surface = normalize(context.clientSurface ||
			(config.chat_cfg_name === 'synapcus-chat-guru' ? 'synapcus-web' : 'public-web'));
		const candidates = entries.filter(entry => {
			if (!entry || entry.enabled === false || !normalize(entry.question)) return false;
			const surfaces = Array.isArray(entry.surfaces) ? entry.surfaces : [];
			return (!surfaces.length || surfaces.includes(surface)) &&
				matchesPage(entry, page) && matchesContextType(entry, page, currentObject) &&
				matchesRoles(entry, context.userRoles || context.roles) &&
				requiredToolsFor(config, entry) !== null;
		});
		if (!candidates.length) return [];

		const dateBucket = new Date().toISOString().slice(0, 10);
		const priorities = [...new Set(candidates.map(entry => Number(entry.priority) || 0))]
			.sort((left, right) => right - left);
		const ordered = priorities.flatMap(priority => {
			const group = candidates.filter(entry => (Number(entry.priority) || 0) === priority);
			const offset = stableIndex(`${page}|${surface}|${dateBucket}|${priority}`, group.length);
			return group.slice(offset).concat(group.slice(0, offset));
		});
		const safeLimit = Math.max(1, Math.min(Number(limit) || 5, 8));
		return ordered.slice(0, safeLimit).map(selected => ({
			id: normalize(selected.id) || `nudge-${stableIndex(selected.question, 100000)}`,
			question: normalize(selected.question),
			prompt: normalize(selected.prompt) || normalize(selected.question),
			contextType: normalize(selected.contextType).toLowerCase() || 'any',
			// Only explicitly curated, answerable data questions may proactively
			// animate the launcher. Other entries remain useful helper questions
			// after the user opens the Guru.
			proactive: selected.proactive === true,
			contextKey: contextIdentity(context, page),
			currentPage: page,
			clientSurface: surface,
			requiredTools: requiredToolsFor(config, selected) || []
		}));
	} catch (error) {
		// Nudges are intentionally optional. Invalid configuration must never
		// affect the chat or the host application.
		return [];
	}
};

export const selectGuruNudge = (chatConfig, context = {}) =>
	selectGuruNudges(chatConfig, context, 1)[0] || null;

